import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { EditorState } from "draft-js";
import { useHistory, useParams } from "react-router-dom";
import { GoPrimitiveDot } from "react-icons/go";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import Swal from "sweetalert2";
import { APP_CONTEXT } from "../../../utils/context";
import PageHeader from "../../../components/PageHeader/PageHeader.component";
import { Row, Column } from "../../../components/Grid/Grid.component";
import Form from "./FAQ.form.component";
import Button from "../../../components/Button/Button.component";
import Error from "../../../components/Error/ErrorMessage.component";
import BorderSelect from "../../../components/Form/BorderSelect/BorderSelect.component";
import Notification from "../../../components/Toast/Toast.component";

// GQL
import {
  UPDATE_FAQ,
  DELETE_FAQ,
} from "../../../graphql/mutations/faq.mutation";
import { GET_FAQ } from "../../../graphql/queries/faq.query";
import { formatGraphqlError } from "../../../utils/api";

const FAQEdit = () => {
  const { setActiveSideNav, setPageTitle } = useContext(APP_CONTEXT);
  const { id } = useParams();
  const [faqData, setFaqData] = useState({
    id: "",
    question: "",
    answer: "",
    status: "ACTIVE",
  });
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );

  const [updatedEditorState, setUpdatedEditorState] = useState(false);

  const [updateFaq, { loading }] = useMutation(UPDATE_FAQ);
  const [deleteFaq] = useMutation(DELETE_FAQ);
  const getFaqData = useQuery(GET_FAQ, {
    fetchPolicy: "no-cache",
    variables: { id },
  });

  const history = useHistory();

  useEffect(() => {
    setActiveSideNav("cm");
    setPageTitle("Update Faq");
  });

  const { register, handleSubmit, errors, setValue, getValues } = useForm();

  const changeStatus = (e) => {
    const tempFaqData = { ...faqData };
    tempFaqData.status = e.target.value;
    setFaqData(tempFaqData);
  };

  const handleDelete = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.value) {
        deleteFaq({
          variables: {
            id: faqData.id,
          },
        })
          .then(() => {
            Swal.fire("Deleted!", "FAQ has been deleted.", "success").then(
              () => {
                history.push("/app/cms/faq");
              }
            );
          })
          .catch((err) =>
            Notification.error({
              title: "Delete FAQ Failed",
              message: formatGraphqlError(err),
            })
          );
      }
    });
  };

  const getColor = () => {
    switch (faqData.status) {
      case "ACTIVE":
        return "#39C568";
      case "ARCHIVE":
        return "#FF003A";
      case "DRAFT":
        return "#707172";
      default:
        return "#707172";
    }
  };

  const onSubmit = (values) => {
    /**
     * Create new Content
     * push to list all content
     */

    updateFaq({
      variables: {
        ...values,
        id: faqData.id,
        answer: stateToHTML(editorState.getCurrentContent()),
      },
    })
      .then(() => {
        Notification.success({
          title: "Update FAQ Succeeded",
          message: "",
        });
        history.push("/app/cms/faq");
      })
      .catch((err) =>
        Notification.error({
          title: "Update FAQ Failed",
          message: formatGraphqlError(err),
        })
      );
  };

  if (getFaqData.loading) {
    return "Loading...";
  }

  if (getFaqData.data && getFaqData.data.faq && !updatedEditorState) {
    setUpdatedEditorState(true);
    setEditorState(
      EditorState.createWithContent(stateFromHTML(getFaqData.data.faq.answer))
    );
  }

  return (
    <>
      <PageHeader title="Update Content" back />
      <Row>
        <Column width="70%" noPad>
          <Form
            register={register}
            errors={errors}
            faqData={faqData}
            setFaqData={setFaqData}
            setValue={setValue}
            getValues={getValues}
            editorState={editorState}
            setEditorState={setEditorState}
            faqEditData={getFaqData.data && getFaqData.data.faq}
          />
        </Column>
        <Column width="30%">
          <BorderSelect
            header="Status"
            name="status"
            label="Please select a status"
            value={faqData.status}
            register={register}
            onChange={changeStatus}
            options={[
              { value: "ACTIVE", text: "Active" },
              { value: "ARCHIVE", text: "Archive" },
              { value: "DRAFT", text: "Draft" },
            ]}
            icon={
              <GoPrimitiveDot style={{ color: getColor(), fontSize: "25px" }} />
            }
            margin="60px 0px 120px 0px"
            required={{
              required: "please enter a Content ID",
              pattern: {
                value: /^[a-zA-Z]+$/,
                message: "field cannot be empty",
              },
            }}
            error={errors.id && <Error message={errors.id.message} />}
          />
          <Button
            width="100%"
            onClick={handleSubmit(onSubmit)}
            type="button"
            kind="secondary"
            value="Save & Update"
            loading={loading}
          />
          <Button
            width="100%"
            onClick={() => handleDelete()}
            type="button"
            value="Archive"
            loading={loading}
          />
          <Button
            width="100%"
            onClick={() => history.goBack()}
            type="button"
            value="Cancel"
            loading={loading}
          />
        </Column>
      </Row>
    </>
  );
};

export default FAQEdit;
